import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X, Heart, LogOut, Settings, Package } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useLang } from '../../context/LangContext';
import './Navbar.css';

export default function Navbar() {
  const { isAuthenticated, user, logout } = useAuth();
  const { cartCount } = useCart();
  const { lang, toggleLang, t } = useLang();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [userMenu, setUserMenu] = useState(false);
  const userRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (userRef.current && !userRef.current.contains(e.target)) setUserMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) { navigate(`/shop?search=${encodeURIComponent(search.trim())}`); setSearch(''); setMenuOpen(false); }
  };

  const navLinks = [
    { to: '/', hi: 'होम', en: 'Home' },
    { to: '/shop', hi: 'दुकान', en: 'Shop' },
    { to: '/astrology', hi: 'ज्योतिष', en: 'Astrology' },
    { to: '/rashifal', hi: 'राशिफल', en: 'Rashifal' },
    { to: '/pandit-booking', hi: 'पंडित बुकिंग', en: 'Pandit' },
  ];

  return (
    <>
      <nav className="navbar">
        <div className="navbar-top">
          <div className="container navbar-inner">
            {/* BRAND */}
            <Link to="/" className="navbar-brand" onClick={() => setMenuOpen(false)}>
              <span className="brand-om">ॐ</span>
              <div className="brand-text">
                <span className="brand-hi">राघवेंद्र पूजन भंडार</span>
                <span className="brand-en">AYODHYA</span>
              </div>
            </Link>

            {/* SEARCH (desktop) */}
            <form className="navbar-search" onSubmit={handleSearch}>
              <Search size={14} />
              <input placeholder={t({ hi: 'उत्पाद खोजें...', en: 'Search products...' })} value={search} onChange={e => setSearch(e.target.value)} />
            </form>

            {/* ACTIONS */}
            <div className="navbar-actions">
              <button className="lang-btn" onClick={toggleLang} title="Toggle Language">
                {lang === 'hi' ? 'EN' : 'हि'}
              </button>

              <Link to="/cart" className="navbar-icon-btn cart-btn">
                <ShoppingCart size={18} />
                {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
              </Link>

              {isAuthenticated ? (
                <div className="user-menu-wrap" ref={userRef}>
                  <button className="navbar-avatar" onClick={() => setUserMenu(v => !v)}>
                    {user?.name?.[0]?.toUpperCase()}
                  </button>
                  {userMenu && (
                    <div className="user-dropdown">
                      <div className="user-dropdown-header">
                        <span className="user-dd-name">{user?.name}</span>
                        <span className="user-dd-email">{user?.email}</span>
                      </div>
                      <Link to="/profile" className="user-dd-item" onClick={() => setUserMenu(false)}><User size={13} /> {t({ hi: 'प्रोफाइल', en: 'Profile' })}</Link>
                      <Link to="/my-orders" className="user-dd-item" onClick={() => setUserMenu(false)}><Package size={13} /> {t({ hi: 'मेरे ऑर्डर', en: 'My Orders' })}</Link>
                      <Link to="/wishlist" className="user-dd-item" onClick={() => setUserMenu(false)}><Heart size={13} /> {t({ hi: 'इच्छा सूची', en: 'Wishlist' })}</Link>
                      {user?.role === 'admin' && <Link to="/admin" className="user-dd-item user-dd-admin" onClick={() => setUserMenu(false)}><Settings size={13} /> Admin Panel</Link>}
                      <button className="user-dd-item user-dd-logout" onClick={() => { logout(); setUserMenu(false); }}><LogOut size={13} /> {t({ hi: 'लॉगआउट', en: 'Logout' })}</button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="btn btn-primary btn-sm navbar-login-btn">
                  {t({ hi: 'लॉगिन', en: 'Login' })}
                </Link>
              )}

              <button className="mobile-menu-btn" onClick={() => setMenuOpen(v => !v)}>
                {menuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* NAV LINKS (desktop) */}
        <div className="navbar-links-bar">
          <div className="container navbar-links-inner">
            {navLinks.map(l => (
              <Link key={l.to} to={l.to} className="nav-link">{t(l)}</Link>
            ))}
            <span className="nav-feature">🚚 ₹499+ Free Shipping</span>
            <span className="nav-feature">🔒 Secure Payment</span>
          </div>
        </div>

        {/* MOBILE MENU */}
        {menuOpen && (
          <div className="mobile-menu">
            <form className="mobile-search" onSubmit={handleSearch}>
              <Search size={14} />
              <input placeholder={t({ hi: 'खोजें...', en: 'Search...' })} value={search} onChange={e => setSearch(e.target.value)} />
              <button type="submit">Go</button>
            </form>
            {navLinks.map(l => (
              <Link key={l.to} to={l.to} className="mobile-nav-link" onClick={() => setMenuOpen(false)}>{t(l)}</Link>
            ))}
            {!isAuthenticated && (
              <div className="mobile-auth-btns">
                <Link to="/login" className="btn btn-primary btn-full" onClick={() => setMenuOpen(false)}>Login</Link>
                <Link to="/register" className="btn btn-outline btn-full" onClick={() => setMenuOpen(false)}>Register</Link>
              </div>
            )}
          </div>
        )}
      </nav>
    </>
  );
}
